import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Box, Typography, Paper, Button, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  LinearProgress, Collapse, Alert, CircularProgress,
  Menu, MenuItem, Divider, Badge, Checkbox, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BarChart as ChartIcon,
  Flag as FlagIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { sprintService } from '../services/api';
import BurndownChart from './BurndownChart';

const PRIORITY_COLORS = {
  low: '#22c55e',
  medium: '#f59e0b',
  high: '#ef4444',
  urgent: '#7c3aed'
};

const STATUS_COLORS = {
  not_started: '#94a3b8',
  in_progress: '#3b82f6',
  completed: '#22c55e',
  blocked: '#ef4444'
};

const STATUS_LABELS = {
  not_started: 'Non démarré',
  in_progress: 'En cours',
  completed: 'Terminé',
  blocked: 'Bloqué'
};

function StoryPointsInput({ value, onChange }) {
  return (
    <TextField
      size="small"
      type="number"
      label="SP"
      value={value || ''}
      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
      inputProps={{ min: 0, max: 100 }}
      sx={{ width: 70 }}
    />
  );
}

function TaskCard({ task, index, onStoryPointsChange }) {
  return (
    <Draggable draggableId={`task-${task.id}`} index={index}>
      {(provided, snapshot) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          elevation={snapshot.isDragging ? 6 : 1}
          sx={{
            p: 1.5,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: snapshot.isDragging ? 'action.selected' : 'background.paper',
            borderLeft: `3px solid ${PRIORITY_COLORS[task.priority] || '#94a3b8'}`,
            cursor: 'grab',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <Box {...provided.dragHandleProps} sx={{ color: 'text.disabled', display: 'flex' }}>
            <DragIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap fontWeight="medium">
              {task.title}
            </Typography>
            {task.assignee_names && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {task.assignee_names}
              </Typography>
            )}
          </Box>
          <Chip
            label={STATUS_LABELS[task.status] || task.status}
            size="small"
            sx={{
              bgcolor: `${STATUS_COLORS[task.status]}20`,
              color: STATUS_COLORS[task.status],
              fontSize: '0.65rem',
              height: 20
            }}
          />
          <StoryPointsInput
            value={task.story_points}
            onChange={(val) => onStoryPointsChange(task.id, val)}
          />
        </Paper>
      )}
    </Draggable>
  );
}

function SprintForm({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState({
    name: '',
    goal: '',
    start_date: '',
    end_date: '',
    capacity: ''
  });

  useEffect(() => {
    if (initial) {
      setForm({
        name: initial.name || '',
        goal: initial.goal || '',
        start_date: initial.start_date?.split('T')[0] || '',
        end_date: initial.end_date?.split('T')[0] || '',
        capacity: initial.capacity || ''
      });
    } else {
      setForm({ name: '', goal: '', start_date: '', end_date: '', capacity: '' });
    }
  }, [initial, open]);

  const handleSave = () => {
    if (!form.name.trim()) return;
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Modifier le sprint' : 'Créer un sprint'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nom du sprint *"
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Sprint 1, Sprint Q1 2026..."
          />
          <TextField
            label="Objectif du sprint"
            fullWidth
            multiline
            rows={2}
            value={form.goal}
            onChange={(e) => setForm({ ...form, goal: e.target.value })}
            placeholder="Ce que l'équipe doit accomplir..."
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Date de début"
              type="date"
              fullWidth
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Date de fin"
              type="date"
              fullWidth
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="Capacité (story points)"
            type="number"
            fullWidth
            value={form.capacity}
            onChange={(e) => setForm({ ...form, capacity: e.target.value })}
            inputProps={{ min: 0 }}
            helperText="Nombre de story points que l'équipe peut absorber sur ce sprint"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
          {initial ? 'Enregistrer' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CompleteSprintDialog({ open, onClose, onConfirm, incompleteCount }) {
  const [moveIncomplete, setMoveIncomplete] = useState(true);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Terminer le sprint</DialogTitle>
      <DialogContent>
        {incompleteCount > 0 ? (
          <Box>
            <Typography variant="body2" gutterBottom>
              Ce sprint contient <strong>{incompleteCount} tâche(s) non terminée(s)</strong>.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant={moveIncomplete ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setMoveIncomplete(true)}
                sx={{ mr: 1 }}
              >
                Remettre dans le backlog
              </Button>
              <Button
                variant={!moveIncomplete ? 'contained' : 'outlined'}
                size="small"
                color="warning"
                onClick={() => setMoveIncomplete(false)}
              >
                Laisser dans le sprint
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2">
            Toutes les tâches sont terminées. Confirmez la clôture du sprint.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" color="success" onClick={() => onConfirm(moveIncomplete)}>
          Terminer le sprint
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SprintSection({ sprint, index, onEdit, onDelete, onStart, onComplete, onStoryPointsChange }) {
  const [expanded, setExpanded] = useState(sprint.status !== 'completed');
  const [anchorEl, setAnchorEl] = useState(null);
  const [showBurndown, setShowBurndown] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  const totalPoints = parseInt(sprint.total_points) || 0;
  const completedPoints = parseInt(sprint.completed_points) || 0;
  const progress = totalPoints > 0 ? Math.round((completedPoints / totalPoints) * 100) : 0;
  const incompleteCount = (sprint.tasks || []).filter(t => t.status !== 'completed').length;

  const statusColor = {
    planning: 'default',
    active: 'primary',
    completed: 'success'
  }[sprint.status] || 'default';

  const statusLabel = {
    planning: 'Planification',
    active: 'Actif',
    completed: 'Terminé'
  }[sprint.status] || sprint.status;

  return (
    <Paper sx={{ mb: 2, overflow: 'hidden' }}>
      {/* Header sprint */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: sprint.status === 'active' ? 'primary.main' : 'background.paper',
          color: sprint.status === 'active' ? 'primary.contrastText' : 'text.primary',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton
          size="small"
          sx={{ color: 'inherit' }}
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        >
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {sprint.name}
            </Typography>
            <Chip label={statusLabel} color={statusColor} size="small" />
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {sprint.tasks?.length || 0} tâche(s) · {completedPoints}/{totalPoints} pts
            </Typography>
          </Box>
          {sprint.goal && (
            <Typography variant="caption" sx={{ opacity: 0.7 }} noWrap>
              🎯 {sprint.goal}
            </Typography>
          )}
          {sprint.start_date && sprint.end_date && (
            <Typography variant="caption" sx={{ opacity: 0.7, ml: 1 }}>
              📅 {new Date(sprint.start_date).toLocaleDateString('fr')} → {new Date(sprint.end_date).toLocaleDateString('fr')}
            </Typography>
          )}
        </Box>

        {/* Progress bar */}
        {totalPoints > 0 && (
          <Box sx={{ width: 100, mr: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={sprint.status === 'active' ? 'inherit' : 'primary'}
              sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" sx={{ opacity: 0.8 }}>{progress}%</Typography>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
          {sprint.status === 'planning' && (
            <Tooltip title="Démarrer le sprint">
              <IconButton size="small" color="success" onClick={() => onStart(sprint.id)}>
                <StartIcon />
              </IconButton>
            </Tooltip>
          )}
          {sprint.status === 'active' && (
            <>
              <Tooltip title="Burndown chart">
                <IconButton size="small" sx={{ color: 'inherit' }} onClick={() => setShowBurndown(!showBurndown)}>
                  <ChartIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Terminer le sprint">
                <IconButton size="small" color="success" onClick={() => setCompleteDialogOpen(true)}>
                  <CompleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          <IconButton size="small" sx={{ color: 'inherit' }} onClick={(e) => setAnchorEl(e.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => { setAnchorEl(null); onEdit(sprint); }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Modifier
          </MenuItem>
          {sprint.status === 'completed' && (
            <MenuItem onClick={() => { setAnchorEl(null); setShowBurndown(!showBurndown); }}>
              <ChartIcon fontSize="small" sx={{ mr: 1 }} /> Burndown
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={() => { setAnchorEl(null); onDelete(sprint.id); }} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Supprimer
          </MenuItem>
        </Menu>
      </Box>

      {/* Burndown Chart */}
      <Collapse in={showBurndown}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <BurndownChart sprintId={sprint.id} />
        </Box>
      </Collapse>

      {/* Tasks */}
      <Collapse in={expanded}>
        <Droppable droppableId={`sprint-${sprint.id}`}>
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                p: 2,
                minHeight: 80,
                bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                transition: 'background-color 0.2s ease'
              }}
            >
              {sprint.tasks?.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Glissez des tâches depuis le backlog ici
                </Typography>
              )}
              {(sprint.tasks || []).map((task, i) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={i}
                  onStoryPointsChange={onStoryPointsChange}
                />
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </Collapse>

      <CompleteSprintDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        onConfirm={(moveIncomplete) => { setCompleteDialogOpen(false); onComplete(sprint.id, moveIncomplete); }}
        incompleteCount={incompleteCount}
      />
    </Paper>
  );
}

function SprintBoard({ projectId }) {
  const [sprints, setSprints] = useState([]);
  const [backlog, setBacklog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sprintFormOpen, setSprintFormOpen] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [backlogExpanded, setBacklogExpanded] = useState(true);
  const [forceStartDialog, setForceStartDialog] = useState({ open: false, sprintId: null, activeSprintName: '', moveIncomplete: true });

  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await sprintService.getByProject(projectId);
      setSprints(res.data.sprints || []);
      setBacklog(res.data.backlog || []);
    } catch (err) {
      setError('Erreur lors du chargement des sprints');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const taskId = parseInt(draggableId.replace('task-', ''));
    const destId = destination.droppableId;
    const sprint_id = destId === 'backlog' ? null : parseInt(destId.replace('sprint-', ''));

    // Mise à jour optimiste locale
    let task = null;

    if (source.droppableId === 'backlog') {
      const newBacklog = [...backlog];
      [task] = newBacklog.splice(source.index, 1);
      setBacklog(newBacklog);
    } else {
      const srcSprintId = parseInt(source.droppableId.replace('sprint-', ''));
      setSprints(prev => prev.map(s => {
        if (s.id === srcSprintId) {
          const newTasks = [...s.tasks];
          [task] = newTasks.splice(source.index, 1);
          return { ...s, tasks: newTasks };
        }
        return s;
      }));
    }

    if (destId === 'backlog') {
      const newBacklog = [...backlog];
      newBacklog.splice(destination.index, 0, { ...task, sprint_id: null });
      setBacklog(newBacklog);
    } else {
      const destSprintId = sprint_id;
      setSprints(prev => prev.map(s => {
        if (s.id === destSprintId) {
          const newTasks = [...s.tasks];
          newTasks.splice(destination.index, 0, { ...task, sprint_id: destSprintId });
          return { ...s, tasks: newTasks };
        }
        return s;
      }));
    }

    try {
      await sprintService.moveTask(taskId, { sprint_id });
    } catch {
      setError('Erreur lors du déplacement de la tâche');
      fetchData(true);
    }
  };

  const handleStoryPointsChange = async (taskId, points) => {
    try {
      await sprintService.moveTask(taskId, { story_points: points });
      fetchData(true);
    } catch {
      setError('Erreur lors de la mise à jour des story points');
    }
  };

  const handleCreateSprint = async (form) => {
    try {
      await sprintService.create(projectId, form);
      setSprintFormOpen(false);
      setSuccess('Sprint créé avec succès');
      fetchData(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création du sprint');
    }
  };

  const handleEditSprint = async (form) => {
    try {
      await sprintService.update(editingSprint.id, form);
      setEditingSprint(null);
      setSprintFormOpen(false);
      setSuccess('Sprint mis à jour');
      fetchData(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteSprint = async (id) => {
    if (!window.confirm('Supprimer ce sprint ? Les tâches seront remises dans le backlog.')) return;
    try {
      await sprintService.delete(id);
      setSuccess('Sprint supprimé');
      fetchData(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Erreur lors de la suppression');
    }
  };

  const handleStartSprint = async (id) => {
    try {
      await sprintService.start(id);
      setSuccess('Sprint démarré !');
      fetchData(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const data = err.response?.data;
      if (data?.activeSprint) {
        setForceStartDialog({ open: true, sprintId: id, activeSprintName: data.activeSprint.name, moveIncomplete: true });
      } else {
        setError(data?.error || 'Erreur lors du démarrage du sprint');
      }
    }
  };

  const handleForceStart = async () => {
    const { sprintId, moveIncomplete } = forceStartDialog;
    setForceStartDialog(d => ({ ...d, open: false }));
    try {
      await sprintService.start(sprintId, { force: true, moveIncomplete });
      setSuccess('Sprint démarré !');
      fetchData(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du démarrage du sprint');
    }
  };

  const handleCompleteSprint = async (id, moveIncomplete) => {
    try {
      await sprintService.complete(id, moveIncomplete);
      setSuccess('Sprint terminé !');
      fetchData(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la clôture du sprint');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const activeSprint = sprints.find(s => s.status === 'active');
  const planningSprints = sprints.filter(s => s.status === 'planning');
  const completedSprints = sprints.filter(s => s.status === 'completed');

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Tableau Agile</Typography>
          <Typography variant="body2" color="text.secondary">
            {sprints.length} sprint(s) · {backlog.length} tâche(s) dans le backlog
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingSprint(null); setSprintFormOpen(true); }}
        >
          Nouveau sprint
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Velocity summary si sprints complétés */}
      {completedSprints.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" gutterBottom>Vélocité de l'équipe</Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {Math.round(
                  completedSprints.reduce((sum, s) => sum + (parseInt(s.completed_points) || 0), 0) /
                  completedSprints.length
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">Moy. points/sprint</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {completedSprints.reduce((sum, s) => sum + (parseInt(s.completed_points) || 0), 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Total points livrés</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {completedSprints.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">Sprints terminés</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        {/* Sprint actif en premier */}
        {activeSprint && (
          <SprintSection
            key={activeSprint.id}
            sprint={activeSprint}
            onEdit={(s) => { setEditingSprint(s); setSprintFormOpen(true); }}
            onDelete={handleDeleteSprint}
            onStart={handleStartSprint}
            onComplete={handleCompleteSprint}
            onStoryPointsChange={handleStoryPointsChange}
          />
        )}

        {/* Sprints en planification */}
        {planningSprints.map((sprint) => (
          <SprintSection
            key={sprint.id}
            sprint={sprint}
            onEdit={(s) => { setEditingSprint(s); setSprintFormOpen(true); }}
            onDelete={handleDeleteSprint}
            onStart={handleStartSprint}
            onComplete={handleCompleteSprint}
            onStoryPointsChange={handleStoryPointsChange}
          />
        ))}

        {/* Backlog */}
        <Paper sx={{ mb: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              bgcolor: 'background.paper'
            }}
            onClick={() => setBacklogExpanded(!backlogExpanded)}
          >
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setBacklogExpanded(!backlogExpanded); }}>
              {backlogExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
            <FlagIcon color="action" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
              Backlog
            </Typography>
            <Chip label={`${backlog.length} tâche(s)`} size="small" variant="outlined" />
          </Box>

          <Collapse in={backlogExpanded}>
            <Droppable droppableId="backlog">
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    p: 2,
                    minHeight: 100,
                    bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  {backlog.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      Le backlog est vide. Créez des tâches depuis les onglets Liste ou Kanban.
                    </Typography>
                  )}
                  {backlog.map((task, i) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      index={i}
                      onStoryPointsChange={handleStoryPointsChange}
                    />
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </Collapse>
        </Paper>

        {/* Sprints terminés */}
        {completedSprints.length > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
              Sprints terminés ({completedSprints.length})
            </Typography>
            {completedSprints.map((sprint) => (
              <SprintSection
                key={sprint.id}
                sprint={sprint}
                onEdit={(s) => { setEditingSprint(s); setSprintFormOpen(true); }}
                onDelete={handleDeleteSprint}
                onStart={handleStartSprint}
                onComplete={handleCompleteSprint}
                onStoryPointsChange={handleStoryPointsChange}
              />
            ))}
          </Box>
        )}
      </DragDropContext>

      <SprintForm
        open={sprintFormOpen}
        onClose={() => { setSprintFormOpen(false); setEditingSprint(null); }}
        onSave={editingSprint ? handleEditSprint : handleCreateSprint}
        initial={editingSprint}
      />

      <Dialog open={forceStartDialog.open} onClose={() => setForceStartDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Sprint déjà actif</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Le sprint <strong>"{forceStartDialog.activeSprintName}"</strong> est en cours.
            Voulez-vous le terminer et démarrer le nouveau sprint ?
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={forceStartDialog.moveIncomplete}
                onChange={(e) => setForceStartDialog(d => ({ ...d, moveIncomplete: e.target.checked }))}
              />
            }
            label="Déplacer les tâches non terminées vers le backlog"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceStartDialog(d => ({ ...d, open: false }))}>Annuler</Button>
          <Button onClick={handleForceStart} color="warning" variant="contained">
            Terminer et démarrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SprintBoard;
